import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import * as Lucide from "lucide-react";
import Seo from "@/components/Seo";

const sym = (c) => (c === "INR" ? "₹" : "$");

export default function InvoicePublicView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/invoice/view/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invoice not found"));
  }, [token]);

  if (err) return <div className="min-h-screen bg-[#05050A] text-slate-200 flex items-center justify-center p-5"><div className="bx-card p-8 text-center max-w-md"><Lucide.AlertOctagon size={36} className="mx-auto text-amber-300" /><h1 className="bx-heading text-2xl font-extrabold mt-3">Invoice not found</h1><p className="text-slate-400 text-sm mt-2">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen bg-[#05050A] text-slate-400 flex items-center justify-center"><Lucide.Loader2 className="animate-spin" /> Loading…</div>;

  const { invoice: inv, company } = data;
  const symb = sym(inv.currency);
  const total = Number(inv.computed?.grand_total || 0);
  const pdfUrl = `${API}/invoice/view/${token}/pdf`;

  return (
    <div className="min-h-screen bg-[#05050A] text-slate-100 py-8 px-4" data-testid="invoice-public-view">
      <Seo title={`Invoice ${inv.invoice_no} — ${company.name}`} description="Invoice issued by BINAREX AND CO" />
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bx-card p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              {company.logo_data_url ? (
                <img src={company.logo_data_url} alt={company.name} style={{ maxHeight: company.logo_height_px || 56, background: "#fff", padding: 4, borderRadius: 6 }} />
              ) : (
                <div className="bx-heading text-2xl font-extrabold">{company.name}</div>
              )}
              <div className="text-xs text-slate-400 mt-3 whitespace-pre-line">{company.address}</div>
              <div className="text-[11px] text-slate-500 mt-1">{[company.phone, company.email, company.website].filter(Boolean).join(" · ")}</div>
              {company.gstin && <div className="text-[11px] text-slate-500 mt-1">GSTIN: <strong>{company.gstin}</strong></div>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-bold">TAX INVOICE</div>
              <div className="bx-heading text-2xl font-extrabold mt-1">{inv.invoice_no}</div>
              <div className="text-xs text-slate-400 mt-1">Issued: {new Date(inv.issued_at).toLocaleDateString()}</div>
              {inv.due_at && <div className="text-xs text-slate-400">Due: {new Date(inv.due_at).toLocaleDateString()}</div>}
              {inv.status === "paid" && <div className="mt-3 inline-block px-3 py-1 rounded-full text-[11px] font-bold border-2 border-emerald-400 text-emerald-300">✓ PAID</div>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-6 pt-6 border-t border-white/5">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Bill to</div>
              <div className="text-sm font-bold text-slate-100 mt-1">{inv.customer.name}</div>
              {inv.customer.address && <div className="text-xs text-slate-400 mt-1 whitespace-pre-line">{inv.customer.address}</div>}
              <div className="text-[11px] text-slate-500 mt-1">{[inv.customer.phone, inv.customer.email].filter(Boolean).join(" · ")}</div>
              {inv.customer.gstin && <div className="text-[11px] text-slate-500 mt-1">GSTIN: {inv.customer.gstin}</div>}
            </div>
            {inv.payment && (
              <div className="rounded-xl p-3 border border-emerald-500/30 bg-emerald-500/5">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300">Payment received</div>
                <div className="text-xs text-slate-200 mt-1"><strong>{inv.payment.method}</strong> · {symb}{Number(inv.payment.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                {inv.payment.transaction_ref && <div className="text-[11px] bx-mono text-purple-300 mt-1">{inv.payment.transaction_ref}</div>}
                <div className="text-[10px] text-slate-500 mt-1">Paid: {new Date(inv.payment.paid_at).toLocaleDateString()}</div>
              </div>
            )}
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 bg-white/5">
                <tr>
                  <th className="text-left p-3">Item</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Rate</th>
                  <th className="text-right p-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {(inv.computed?.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="p-3">
                      <div className="text-slate-100">{it.name}</div>
                      {it.description && <div className="text-[10px] text-slate-500">{it.description}</div>}
                    </td>
                    <td className="text-right p-3 text-slate-400">{it.quantity}</td>
                    <td className="text-right p-3 bx-mono">{symb}{Number(it.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="text-right p-3 font-semibold text-purple-300 bx-mono">{symb}{Number(it.line_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 max-w-xs ml-auto text-sm space-y-1">
            <Row label="Subtotal" value={`${symb}${Number(inv.computed?.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
            {inv.computed?.discount_total > 0 && <Row label="Discount" value={`− ${symb}${Number(inv.computed.discount_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} rose />}
            {inv.computed?.cgst_total > 0 && <Row label="CGST" value={`${symb}${Number(inv.computed.cgst_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />}
            {inv.computed?.sgst_total > 0 && <Row label="SGST" value={`${symb}${Number(inv.computed.sgst_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />}
            {inv.computed?.igst_total > 0 && <Row label="IGST" value={`${symb}${Number(inv.computed.igst_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />}
            <div className="flex justify-between pt-2 border-t border-white/10 mt-2">
              <span className="font-bold">Grand Total</span>
              <span className="bx-mono font-extrabold text-purple-300 text-lg">{symb}{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {(inv.notes || inv.terms) && (
            <div className="mt-6 pt-5 border-t border-white/5 grid sm:grid-cols-2 gap-5 text-xs">
              {inv.notes && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Notes</div><div className="text-slate-300 mt-1 whitespace-pre-line">{inv.notes}</div></div>}
              {inv.terms && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Terms</div><div className="text-slate-400 mt-1 whitespace-pre-line">{inv.terms}</div></div>}
            </div>
          )}

          <div className="mt-7 pt-5 border-t border-white/5 grid sm:grid-cols-2 gap-5">
            <SigBlock label="Customer" sig={inv.customer_signature_data_url} signerName={inv.customer_signer_name || inv.customer?.name} signedAt={inv.customer_signed_at} />
            <SigBlock label={`Authorised — ${company.name}`} sig={inv.admin_signature_data_url || company.signature_data_url} signedAt={inv.admin_signed_at} />
          </div>

          <div className="mt-6 flex gap-2 justify-center flex-wrap">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="bx-btn-primary" data-testid="public-view-pdf"><Lucide.Download size={14} /> Download PDF</a>
            <button onClick={() => window.print()} className="bx-btn-ghost"><Lucide.Printer size={14} /> Print</button>
          </div>
        </motion.div>
        <p className="text-[10px] text-slate-500 text-center mt-4">Powered by {company.name}</p>
      </div>
    </div>
  );
}

function Row({ label, value, rose }) {
  return <div className="flex justify-between"><span className="text-slate-400">{label}</span><span className={`bx-mono ${rose ? "text-rose-300" : ""}`}>{value}</span></div>;
}

function SigBlock({ label, sig, signerName, signedAt }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      {sig ? (
        <div className="rounded-lg p-3 mt-1" style={{ background: "white" }}>
          <img src={sig} alt={label} style={{ maxHeight: 60 }} />
        </div>
      ) : <div className="rounded-lg border border-dashed border-white/15 p-6 mt-1 text-center text-xs text-slate-500">Not signed</div>}
      {signerName && <div className="text-xs text-slate-200 mt-1">{signerName}</div>}
      {signedAt && <div className="text-[10px] text-slate-500">{new Date(signedAt).toLocaleDateString()}</div>}
    </div>
  );
}
